import { useState } from "react";
import { Loader2, Mail, X } from "lucide-react";

/**
 * Email challenge shown before downloading a signed document or certificate
 * from the public verification endpoints. The server only releases the file
 * when the entered email matches a party to the envelope (a recipient or the
 * sender), so possession of the envelope ID alone is not enough to obtain
 * the parties' personal data.
 *
 * `onSubmit(email)` performs the download and must throw/reject on failure;
 * the dialog closes itself on success.
 */
export const DownloadEmailChallenge = ({ open, onClose, onSubmit }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const close = () => {
    setError("");
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await onSubmit(email.trim());
      close();
    } catch (err) {
      const status = err?.response?.status;
      // 422 = validation (malformed email), 403 = not a party
      if (status === 403 || status === 400 || status === 422) {
        setError(
          "This email address does not match a party to this document. Enter the address the document was sent to or from.",
        );
      } else if (status === 429) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError("Download failed. Please try again later.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="w-full max-w-md rounded-xl bg-card text-card-foreground shadow-xl p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-challenge-title"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <h2
              id="download-challenge-title"
              className="text-lg font-semibold"
            >
              Confirm your email
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          This file contains personal information about the parties to the
          document. To download it, enter the email address you used with this
          document — as a signer, recipient or sender.
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="email"
            required
            autoFocus
            aria-label="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />

          {error && (
            <p className="mt-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 text-sm border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !email.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Download
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DownloadEmailChallenge;
